/**
=========================================================
* BASE: Learning Repository - v1.0.0
=========================================================

* Copyright 2022 Sean Dooley (https://www.dooley-dev.com)

Coded by www.dooley-dev.com

 =========================================================

* The above copyright notice shall be included in substantial portions of the Software.
*/
import React, { useEffect } from 'react';
import {
  useNavigate,
  useParams,
  useOutletContext,
  useLocation
} from 'react-router-dom';
// redux
import { useDispatch, useSelector } from 'react-redux';
// actions
import {
  getListing,
  getEditInstance,
  addInstance,
  updateInstance,
  deleteInstance,
  newInstance
} from '../../actions';
import { commentCollectionsConfig } from './CommentCollectionsConfig';
import { selectUser } from '../../store/userSlice';
// BASE components
import NavLinkAdapter from '../../../@BASE/core/NavLinkAdapter';
import BASELoading from '../../../@BASE/core/BASELoading';
import BASESvgIcon from '../../../@BASE/core/BASESvgIcon';
// React hook form
import _ from '../../../@lodash';
import * as yup from 'yup';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
// Material UI Components
import Button from '@mui/material/Button';
import Box from '@mui/system/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Switch from '@mui/material/Switch';
// Custom components
import NumberedHeading from '../forms/NumberedHeading';
import CommentScopeSelect from '../../main/comments/CommentScopeSelect';
import CommentStatusSelect from '../../main/comments/CommentStatusSelect';
import DeleteModal from '../crud/DeleteModal';

/**
 * Form Validation Schema
 */
const schema = yup.object().shape({
  content: yup.string().required('You must enter a comment')
});

const CommentForm = ({ isIMI = false, collection = 'comments' }) => {
  const { instance, reviews } = useOutletContext();
  const [comment, setComment] = React.useState(null);
  const user = useSelector(selectUser);
  const routeParams = useParams();
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname, search } = location;
  const pathArray = pathname.split('/');
  const resource = pathArray[1];
  const comment_heirarchy = ['project', 'course', 'lesson', 'section', 'page'];
  const pluralToSingular = {
    projects: 'project',
    courses: 'course',
    lessons: 'lesson',
    sections: 'section',
    pages: 'page'
  };
  const level = collection === 'comments' ? pluralToSingular[resource] : 'page';
  const dispatchString = collection;
  
  let navigateDestination;
  switch (collection) {
    case 'imi_comments':
      navigateDestination = resource === 'imi-comments' ? `/imi-comments/pages/${routeParams.id}${search}` : `/${resource}/${routeParams.id}/imi-comments${search}`;
      break;
    case 'pre_trial_comments':
      navigateDestination = resource === 'pre-trial-comments' ? `/pre-trial-comments/pages/${routeParams.id}${search}` : `/${resource}/${routeParams.id}/pre-trial-comments${search}`;
      break;
    default:
      navigateDestination = `/${resource}/${routeParams.id}/comments${search}`;
  }

  const getCurrentReview = () => {
    // get the current date
    let currentDate = new Date();
    // find the active review
    let currentReview = reviews.find((review) => {
      let startDate = new Date(review.start);
      let endDate = new Date(review.end);

      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(23, 59, 59, 999);

      return startDate <= currentDate && endDate >= currentDate;
    });
    // ensure currentReview is not null before returning
    

    return currentReview;
  };

  const currentReview = getCurrentReview();

  const {
    control,
    watch,
    reset,
    handleSubmit,
    formState,
    setValue,
    getValues
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(schema)
  });

  const { isValid, dirtyFields, errors } = formState;

  const form = watch();

  const handleGetComment = async () => {
    try {
      const response = await dispatch(
        routeParams.commentId === 'new'
          ? newInstance(dispatchString)()
          : getEditInstance(dispatchString)(routeParams.commentId)
      ).unwrap();

      // response.user_id = user.id;
      if (routeParams.commentId === 'new') {
        response.scope = level;
      }

      setComment(response);
    } catch (err) {
      console.log(err.message);
    }
  };

  useEffect(() => {
    handleGetComment();
  }, [dispatch, routeParams]);

  useEffect(() => {
    reset({ ...comment });
  }, [comment, reset]);

  function onSubmit(data) {
    // set user
    data.user = user._id || user.id;
    // default internality to false if user is not internal, otherwise use toggle
    data.internality = user.internal ? form.internality : false;
    // need to set approved bool based on dropdown and 2 options can set it to true
    data.approved =
      data.status == 'approved' || data.status == 'approvedWithComments'
        ? true
        : false;
    // need to collect lesson_id in order to figure out review period
    let lesson_id = null;
    // set project which is needed by backend
    // Loop through the hierarchy to navigate the nested structure

    if (routeParams.commentId === 'new') {
      let parent = instance;
      let currentLevel = null;

      while (parent) {
        // this will check if child key exists and then add 1 to return the actual current level instead of the child level.
        currentLevel =
          comment_heirarchy[
            comment_heirarchy.findIndex((level) =>
              parent.hasOwnProperty(level)
            ) + 1
          ];
        if (!currentLevel) {
          console.error(
            'Could not determine the current level in the hierarchy.'
          );
          break;
        }

        if (currentLevel == 'lesson') {
          lesson_id = parent.id;
        }

        if (parent.hasOwnProperty('id')) {
          data[currentLevel] = parent.id;
        }
        // } else {
        //   console.log(`Didn't find ${currentLevel}`);
        // }

        // this will get the child object for being the next parent.
        parent =
          parent[
            comment_heirarchy.find((level) => parent.hasOwnProperty(level))
          ];
      }
    } else {
      let current = instance;
      for (let i = comment_heirarchy.length - 1; i >= 0; i--) {
        const key = comment_heirarchy[i];

        // If the current level exists, move deeper
        if (current && current[key]) {
          current = current[key];

          // If the key is "project" and has "id" or "_id", set it in data and exit the loop
          if (key === 'project') {
            if (current.id) {
              data.project = current.id;
            } else if (current._id) {
              data.project = current._id;
            }
            break; // Exit the loop once project.id or project._id is set
          }
        }
      }
      // loop through heirarchy in reverse till matching the intended scope to set information along with parents
      for (let i = comment_heirarchy.length - 1; i >= 0; i--) {
        const key = comment_heirarchy[i];

        // if comment lacks the key, skip this iteration
        if (!comment.hasOwnProperty(key)) {
          continue;
        }

        if (comment[key]['_id']) {
          data[key] = comment[key]['_id'];
        } else if (comment[key]['id']) {
          data[key] = comment[key]['id'];
        }
      }
    }
    // get currentReview
    let currentReview = null;
    // set currentReview based on id
    currentReview = getCurrentReview();
    // set in data for sending
    data.review = currentReview.id;

    // this will add component to the list
    const searchParams = new URLSearchParams(location.search);
    const cValue = searchParams.get('c');
    if (cValue) {
      data.component = cValue;
    }

    console.log('Submitting comment with data:', data);

    if (routeParams.commentId === 'new') {
      dispatch(addInstance(dispatchString)(data)).then(({ payload }) => {
        navigate(navigateDestination);
      });
    } else {
      data.id = routeParams.commentId;

      const updatedComment = {
        id: data.id,
        scope: data.scope,
        status: data.status,
        title: data.title,
        content: data.content,
        internality: data.internality,
        review: data.review
      };

      dispatch(updateInstance(dispatchString)(updatedComment)).then(({ payload }) => {
        navigate(navigateDestination);
      });
    }
  }

  const handleRemoveComment = () => {
    let data = {
      data: {
        user: user._id || user.id
      },
      id: comment.id
    };

    dispatch(deleteInstance(dispatchString)(data)).then(() => {
      navigate(navigateDestination);
    });
  };


  

  if (_.isEmpty(form) || !comment || !instance || !reviews) {
    return <BASELoading />;
  }

  return (
    <>
      <IconButton
        className="absolute top-0 right-0 my-16 mx-32 z-10"
        sx={{ color: 'black' }}
        component={NavLinkAdapter}
        to="../"
        size="large"
      >
        <BASESvgIcon>heroicons-outline:x</BASESvgIcon>
      </IconButton>
      <div className="relative flex flex-col flex-auto mt-64 px-24 sm:px-48">
        <Typography className="mt-12 text-2xl font-bold truncate">
          {routeParams.commentId === 'new' ? 'Create' : 'Edit'} Comment
        </Typography>
        <Divider className="mt-16 mb-24" />

        <NumberedHeading
          number="1"
          title="Scope"
          instructions="Select an applicable scope for the comment."
        />

        <Controller
          name="scope"
          control={control}
          render={({ field }) => (
            <CommentScopeSelect
              className="mt-32"
              {...field}
              level={comment.scope}
              comment_heirarchy={comment_heirarchy}
              required
              disabled={routeParams.commentId !== 'new'}
            />
          )}
        />

        <NumberedHeading
          number="2"
          title="Status"
          instructions="Select an applicable status for the comment."
        />

        <Controller
          name="status"
          control={control}
          render={({ field }) => (
            <CommentStatusSelect className="mt-32" {...field} required />
          )}
        />

        <NumberedHeading
          number="3"
          title="Title"
          instructions="Enter the title for this comment."
        />

        <Controller
          control={control}
          name="title"
          render={({ field }) => (
            <>
              <TextField
                {...field}
                className="mt-16 mb-16"
                label="Title"
                placeholder="Title"
                fullWidth
              />
            </>
          )}
        />

        <NumberedHeading
          number="4"
          title="Comment"
          instructions="Enter the comment using the controls below."
        />

        <Controller
          name="content"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              className="mt-32 mb-16"
              id="content"
              label="Content"
              type="text"
              multiline
              rows={10}
              error={!!errors.name}
              helperText={errors?.name?.message}
              variant="outlined"
              required
              fullWidth
            />
          )}
        />

        {user.internal && (
          <>
            <NumberedHeading
              number="5"
              title="Internality"
              instructions="Default to an internal comment. Turn off if not internal."
            />

            <Controller
              name="internality"
              control={control}
              defaultValue={user.internal || false}
              render={({ field }) => (
                <Switch
                  {...field}
                  id="internality"
                  checked={field.value}
                  onChange={(event) => field.onChange(event.target.checked)}
                />
              )}
            />
          </>
        )}
      </div>
      <Box
        className="flex items-center mt-40 py-14 pr-16 pl-4 sm:pr-48 sm:pl-36 border-t"
        sx={{ backgroundColor: 'background.default' }}
      >
        {routeParams.commentId !== 'new' && (
          <DeleteModal onDelete={handleRemoveComment} resource="comment" />
        )}
        <Button className="ml-auto" component={NavLinkAdapter} to={-1}>
          Cancel
        </Button>
        <Button
          className="ml-8"
          variant="contained"
          color="secondary"
          disabled={_.isEmpty(dirtyFields) || !isValid}
          onClick={handleSubmit(onSubmit)}
        >
          Save
        </Button>
      </Box>
    </>
  );
};

export default CommentForm;
